import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowPathIcon,
  CloudArrowDownIcon,
  PlayIcon,
  QueueListIcon,
  CheckCircleIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { audienceMemberApi } from '../../api';
import { toast } from 'react-toastify';

const POLL_INTERVAL_MS = 5000;

/**
 * Panel displayed at the top of the Audience Members tab.
 * Lets an admin trigger the two on-demand Quartz jobs that bulk-fetch
 * financial data from ANAF and monitor the queue depth.
 *
 * Flow:
 *  1. "Populate Queue"   → finds members with a CUI that haven't been queried → enqueues them
 *  2. "Process Batch"    → pulls ≤10 items from the queue, calls ANAF, writes results back
 */
const FinancialDataSyncPanel = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // { running, currentBatchSize, queueSize, membersWithoutData }
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [triggeringPopulate, setTriggeringPopulate] = useState(false);
  const [triggeringProcess, setTriggeringProcess] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const pollRef = useRef(null);

  const fetchJobStatus = useCallback(async (silent = false) => {
    if (!silent) setLoadingStatus(true);
    try {
      const status = await audienceMemberApi.getFinancialJobStatus();
      setJobStatus(status);
      return status;
    } catch (err) {
      console.error('Failed to fetch financial job status', err);
      return null;
    } finally {
      if (!silent) setLoadingStatus(false);
    }
  }, []);

  // Start / stop the 5-second polling based on whether a job is running
  useEffect(() => {
    if (jobStatus?.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => fetchJobStatus(true), POLL_INTERVAL_MS);
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobStatus?.running, fetchJobStatus]);

  // Initial load
  useEffect(() => {
    fetchJobStatus();
  }, [fetchJobStatus]);

  const handlePopulateQueue = async () => {
    setTriggeringPopulate(true);
    try {
      await audienceMemberApi.triggerFinancialDataPopulate();
      toast.success('Populate job triggered — eligible members are being enqueued.');
      setLastAction({ type: 'populate', time: new Date() });
      setTimeout(() => fetchJobStatus(true), 1500);
    } catch (err) {
      console.error('Failed to trigger populate job', err);
      toast.error('Failed to trigger populate job');
    } finally {
      setTriggeringPopulate(false);
    }
  };

  const handleProcessBatch = async () => {
    setTriggeringProcess(true);
    try {
      await audienceMemberApi.triggerFinancialDataProcess();
      toast.success('Process job triggered — fetching from ANAF.');
      setLastAction({ type: 'process', time: new Date() });
      // Poll immediately so the "running" state is picked up quickly
      setTimeout(async () => {
        const status = await fetchJobStatus(true);
        // If the job is already running, the useEffect above will start the interval
        if (status && !status.running) {
          // Job finished very quickly — refresh once more after a short delay
          setTimeout(() => fetchJobStatus(true), 2000);
        }
      }, 800);
    } catch (err) {
      console.error('Failed to trigger process job', err);
      toast.error('Failed to trigger process job');
    } finally {
      setTriggeringProcess(false);
    }
  };

  const formatTime = (date) =>
    date
      ? date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : null;

  const queueSize = jobStatus?.queueSize ?? null;
  const isRunning = jobStatus?.running ?? false;
  const currentBatchSize = jobStatus?.currentBatchSize ?? 0;
  const remainingInBatch = jobStatus?.remainingInBatch ?? 0;
  const membersWithoutData = jobStatus?.membersWithoutData ?? null;
  const queueEmpty = queueSize !== null && queueSize === 0;
  const queueHasItems = queueSize !== null && queueSize > 0;

  return (
    <div className="mb-6 rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-sm">
      {/* Header — always visible */}
      <div
        className="flex cursor-pointer items-center justify-between px-5 py-3"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <CloudArrowDownIcon className="h-5 w-5 text-indigo-600" />
          <h3 className="text-sm font-semibold text-indigo-900">ANAF Financial Data Sync</h3>
          {isRunning && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
              <BoltIcon className="h-3 w-3 animate-pulse" />
              Processing {remainingInBatch} / {currentBatchSize} members…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Queue badge (always visible even when collapsed) */}
          <button
            onClick={(e) => { e.stopPropagation(); fetchJobStatus(); }}
            disabled={loadingStatus}
            title="Refresh status"
            className="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 disabled:opacity-50"
          >
            <QueueListIcon className="h-3.5 w-3.5" />
            {queueSize === null ? (
              <span>Queue: —</span>
            ) : (
              <span>
                Queue:{' '}
                <span className={queueHasItems ? 'text-amber-600 font-bold' : 'text-green-600'}>
                  {queueSize}
                </span>
              </span>
            )}
            <ArrowPathIcon className={`h-3 w-3 ${loadingStatus || isRunning ? 'animate-spin' : ''}`} />
          </button>
          {collapsed ? (
            <ChevronDownIcon className="h-4 w-4 text-indigo-400" />
          ) : (
            <ChevronUpIcon className="h-4 w-4 text-indigo-400" />
          )}
        </div>
      </div>

      {/* Collapsible body */}
      {!collapsed && (
        <div className="px-5 pb-5">
          {/* Stats row */}
          <div className="mb-4 grid grid-cols-4 gap-3 text-center">
            <Stat
              label="Without Data"
              value={membersWithoutData ?? '—'}
              color={membersWithoutData > 0 ? 'text-amber-600' : 'text-green-600'}
            />
            <Stat
              label="In Queue"
              value={queueSize ?? '—'}
              color={queueHasItems ? 'text-amber-600' : 'text-green-600'}
            />
            <Stat
              label="Current Batch"
              value={isRunning ? `${remainingInBatch} / ${currentBatchSize}` : '—'}
              color={isRunning ? 'text-amber-600' : 'text-gray-400'}
            />
            <Stat
              label="Job Status"
              value={
                jobStatus === null ? '—'
                  : isRunning ? 'Running ⚡'
                  : queueEmpty ? 'Idle ✓'
                  : 'Idle'
              }
              color={isRunning ? 'text-amber-600' : queueEmpty ? 'text-green-600' : 'text-gray-600'}
            />
          </div>

          {/* Auto-refresh notice */}
          {isRunning && (
            <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              Auto-refreshing every 5 s while job is running…
            </div>
          )}

          {/* How-to callout */}
          <div className="mb-4 rounded-lg bg-white/70 border border-indigo-100 px-3 py-2 text-xs text-indigo-700">
            <span className="font-semibold">How it works: </span>
            First run <span className="font-medium">Populate Queue</span> to enqueue all members with a CUI that
            haven't been fetched yet, then click <span className="font-medium">Process Batch</span> repeatedly
            until the queue empties (each run fetches up to 100 members from ANAF).
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePopulateQueue}
              disabled={triggeringPopulate || triggeringProcess || isRunning}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
            >
              <QueueListIcon className={`h-4 w-4 ${triggeringPopulate ? 'animate-pulse' : ''}`} />
              {triggeringPopulate ? 'Enqueueing…' : 'Populate Queue'}
            </button>

            <button
              onClick={handleProcessBatch}
              disabled={triggeringProcess || triggeringPopulate || queueEmpty || isRunning}
              className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
              title={queueEmpty ? 'Queue is empty — populate first' : isRunning ? 'Job already running' : undefined}
            >
              <PlayIcon className={`h-4 w-4 ${triggeringProcess ? 'animate-pulse' : ''}`} />
              {triggeringProcess ? 'Triggering…' : isRunning ? 'Running…' : 'Process Batch (100)'}
            </button>
          </div>

          {/* Last action */}
          {lastAction && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
              <CheckCircleIcon
                className={`h-3.5 w-3.5 ${lastAction.type === 'populate' ? 'text-indigo-500' : 'text-purple-500'}`}
              />
              Last {lastAction.type === 'populate' ? 'populate' : 'process'} triggered at{' '}
              {formatTime(lastAction.time)}
              {' — '}
              <button onClick={() => fetchJobStatus()} className="underline hover:text-indigo-600">
                refresh now
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Small reusable stat cell
const Stat = ({ label, value, color }) => (
  <div className="rounded-lg bg-white/60 border border-indigo-100 px-3 py-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`mt-0.5 text-lg font-bold ${color}`}>{value}</p>
  </div>
);

export default FinancialDataSyncPanel;

